import type { PainelData } from "../../../services/painelService";
import {
  agruparItensEntregaPorPedido,
} from "./agruparPedidosPainel";
import {
  resolveSegundosNoForno,
  resolveTempoColunaEntrega,
  resolveTempoColunaFilaMontagem,
} from "./painelCardUrgencia";
import { segundosDesdeInicio } from "./temposPainel";
import { resolveSalaoProducaoLabels } from "./origemPainel";

export const PAINEL_ATRASADO_MIN_SEG = 10 * 60;

export type PainelStatusAtrasado =
  | "aguardando_montagem"
  | "em_montagem"
  | "em_forno"
  | "aguardando_entrega"
  | "em_entrega"
  | "aguardando_borda"
  | "aguardando_bordeiro";

export const PAINEL_STATUS_ATRASADO_LABEL: Record<
  PainelStatusAtrasado,
  string
> = {
  aguardando_montagem: "Aguardando montagem",
  em_montagem: "Em montagem",
  em_forno: "Em forno",
  aguardando_entrega: "Aguardando entrega",
  em_entrega: "Em entrega",
  aguardando_borda: "Aguardando borda",
  aguardando_bordeiro: "Aguardando bordeiro",
};

export interface PainelPedidoAtrasado {
  key: string;
  displayId: number;
  orderId: number;
  status: PainelStatusAtrasado;
  statusLabel: string;
  segundos: number;
  customerName: string | null;
  /** Rótulo sem Pedido # para salão FoodFly (mesa/comanda). */
  tituloSalao?: string | null;
}

function tituloSalaoFoodfly(opts: {
  origem?: string | null;
  tipo?: string | null;
  mesaId?: string | number | null;
  comandaId?: string | number | null;
  identificador?: string | null;
}): string | null {
  const isSalao =
    opts.origem === "salao" ||
    opts.tipo === "salao_salgada" ||
    opts.tipo === "salao_doce";
  if (!isSalao) return null;
  const labels = resolveSalaoProducaoLabels({
    mesaId: opts.mesaId,
    comandaId: opts.comandaId,
    identificador: opts.identificador,
  });
  if (!labels.mesa && !labels.comandaLabel) return null;
  return [labels.mesa, labels.comandaLabel, labels.cliente]
    .filter(Boolean)
    .join(" · ");
}

function upsertAtrasado(
  map: Map<string, PainelPedidoAtrasado>,
  item: Omit<PainelPedidoAtrasado, "key" | "statusLabel">,
): void {
  if (item.segundos < PAINEL_ATRASADO_MIN_SEG) return;
  const key = `${item.displayId}:${item.status}`;
  const prev = map.get(key);
  if (prev && prev.segundos >= item.segundos) return;
  map.set(key, {
    ...item,
    key,
    statusLabel: PAINEL_STATUS_ATRASADO_LABEL[item.status],
  });
}

/**
 * Itens do painel parados há 10+ min na etapa atual.
 * Uma linha por (pedido × status); o botão conta displayIds únicos.
 */
export function listarPedidosAtrasadosPainel(
  data: PainelData | null,
  nowMs: number,
): PainelPedidoAtrasado[] {
  if (!data) return [];

  const map = new Map<string, PainelPedidoAtrasado>();

  for (const item of data.aguardandoMontagem) {
    const segundos = resolveTempoColunaFilaMontagem(item, nowMs);
    if (segundos == null) continue;
    upsertAtrasado(map, {
      displayId: item.displayId,
      orderId: item.orderId,
      status: "aguardando_montagem",
      segundos,
      customerName: item.pedidoIntegracao?.customerName ?? null,
      tituloSalao: tituloSalaoFoodfly({
        origem: item.origem,
        tipo: item.tipo,
        mesaId: item.pedidoFoodfly?.mesaId,
        comandaId: item.pedidoFoodfly?.comandaId,
        identificador: item.pedidoFoodfly?.identificador,
      }),
    });
  }

  for (const item of data.emMontagem) {
    const segundos = resolveTempoColunaFilaMontagem(item, nowMs);
    if (segundos == null) continue;
    upsertAtrasado(map, {
      displayId: item.displayId,
      orderId: item.orderId,
      status: "em_montagem",
      segundos,
      customerName: item.pedidoIntegracao?.customerName ?? null,
      tituloSalao: tituloSalaoFoodfly({
        origem: item.origem,
        tipo: item.tipo,
        mesaId: item.pedidoFoodfly?.mesaId,
        comandaId: item.pedidoFoodfly?.comandaId,
        identificador: item.pedidoFoodfly?.identificador,
      }),
    });
  }

  for (const item of data.emForno) {
    const segundos = resolveSegundosNoForno(item, nowMs);
    if (segundos == null) continue;
    upsertAtrasado(map, {
      displayId: item.displayId,
      orderId: item.orderId,
      status: "em_forno",
      segundos,
      customerName: item.pedidoIntegracao?.customerName ?? null,
      tituloSalao: tituloSalaoFoodfly({
        origem: item.origem,
        mesaId: item.salaoMesaId,
        comandaId: item.salaoComandaId,
        identificador: item.salaoIdentificador,
      }),
    });
  }

  for (const grupo of agruparItensEntregaPorPedido(data.aguardandoEntrega)) {
    const principal = grupo[0];
    if (!principal) continue;
    const segundos = resolveTempoColunaEntrega(
      grupo,
      "aguardando_entrega",
      nowMs,
    );
    if (segundos == null) continue;
    upsertAtrasado(map, {
      displayId: principal.displayId,
      orderId: principal.orderId,
      status: "aguardando_entrega",
      segundos,
      customerName: principal.pedidoIntegracao?.customerName ?? null,
    });
  }

  for (const grupo of agruparItensEntregaPorPedido(data.emEntrega)) {
    const principal = grupo[0];
    if (!principal) continue;
    const segundos = resolveTempoColunaEntrega(grupo, "em_entrega", nowMs);
    if (segundos == null) continue;
    upsertAtrasado(map, {
      displayId: principal.displayId,
      orderId: principal.orderId,
      status: "em_entrega",
      segundos,
      customerName: principal.pedidoIntegracao?.customerName ?? null,
    });
  }

  for (const item of data.aguardandoBorda ?? []) {
    const segundos = segundosDesdeInicio(item.paradoDesde, nowMs);
    const isSalao =
      item.tipo === "salao_salgada" || item.tipo === "salao_doce";
    upsertAtrasado(map, {
      displayId: item.displayId,
      orderId: item.orderId,
      status: "aguardando_borda",
      segundos,
      customerName: item.customerName,
      tituloSalao: isSalao
        ? ["Salão", item.customerName].filter(Boolean).join(" · ")
        : null,
    });
  }

  for (const item of data.paozinhosAguardandoBordeiro ?? []) {
    const segundos = segundosDesdeInicio(item.createdAt, nowMs);
    const isSalao = item.origem === "salao";
    const cliente = isSalao
      ? [item.salaoIdentificador, item.salaoMesaId]
          .map((v) => (v != null ? String(v).trim() : ""))
          .find(Boolean) ?? null
      : null;
    upsertAtrasado(map, {
      displayId: item.displayId,
      orderId: item.orderId,
      status: "aguardando_bordeiro",
      segundos,
      customerName: cliente,
      tituloSalao: isSalao
        ? ["Salão", cliente].filter(Boolean).join(" · ")
        : null,
    });
  }

  return Array.from(map.values()).sort((a, b) => {
    if (b.segundos !== a.segundos) return b.segundos - a.segundos;
    return a.displayId - b.displayId;
  });
}

export function contarPedidosAtrasadosUnicos(
  itens: PainelPedidoAtrasado[],
): number {
  return new Set(itens.map((i) => i.displayId)).size;
}
